/**
 * Web Serial protocol client for the VPSI ESP32 sensor.
 *
 * Talks to the device over a USB serial connection using the browser
 * Web Serial API.  Sends text commands (newline-terminated) and
 * parses text and binary responses.
 */

import { crc32hex } from './crc32';
import mapReadings from './mapReadings';

export class ReadingsError extends Error {
  constructor(reason) {
    super(reason);
    this.name = 'ReadingsError';
    this.reason = reason;
  }
}

const DEFAULT_BAUD = 115200;
const FAST_BAUD = 921600;
const COMMAND_TIMEOUT_MS = 3000;
const HELLO_MAX_ATTEMPTS = 3;
// Opening the port resets the ESP32 (DTR/RTS); it is then deaf for a few
// seconds while it boots (setup() delay + sensor init) and cannot answer a
// HELLO sent during that window. Rather than send one HELLO and wait out the
// whole attempt, we re-send ("poke") every POKE_INTERVAL_MS so a freshly-booted
// device answers the very next probe instead of stalling until the next attempt.
const HELLO_POKE_INTERVAL_MS = 350;
const TRANSFER_TIMEOUT_MS = 15000; // Longer timeout for file transfers at 115200 baud

class SerialClient extends EventTarget {
  constructor() {
    super();
    this._port = null;
    this._reader = null;
    this._writer = null;
    this._connected = false;
    this._deviceInfo = null;
    this._negotiatedBaud = null;
    this._buffer = new Uint8Array(0);
    this._readLoopRunning = false;
    this._logListeners = [];
    this._commandQueue = Promise.resolve(); // Serializes all commands
    this._pendingRead = null; // in-flight reader.read() reused across timeouts
  }

  /**
   * Register a callback to receive serial log entries.
   * Each entry: { direction: 'tx'|'rx', type: 'text'|'binary', data: string, timestamp: Date }
   */
  onLog(callback) {
    this._logListeners.push(callback);
    return () => {
      this._logListeners = this._logListeners.filter((cb) => cb !== callback);
    };
  }

  _log(direction, data, type = 'text') {
    const entry = { direction, type, data, timestamp: new Date() };
    for (const cb of this._logListeners) {
      try { cb(entry); } catch (_) {}
    }
  }

  // ---- Connection ----

  /**
   * Open the serial port, send HELLO, and negotiate baud rate.
   * @param {SerialPort} port  The port obtained from navigator.serial.requestPort()
   * @param {(attempt: number, maxAttempts: number) => void} [onProgress]
   *   Called at the start of each HELLO handshake attempt (1-based) so callers
   *   can show progress instead of a stalled UI.
   */
  async connect(port, onProgress) {
    if (this._connected) throw new Error('Already connected');

    this._port = port;

    // Try each candidate baud in order. 115200 (DEFAULT_BAUD) is the normal
    // rate and is tried first, so a normal device connects exactly as before
    // with no extra open. Only if the device never answers HELLO at 115200
    // (e.g. a Linux/CH340 unit left running at the fast OTA baud) do we
    // close, reopen at 921600 (FAST_BAUD), and retry the same handshake.
    let lastError = null;
    for (const baud of [DEFAULT_BAUD, FAST_BAUD]) {
      await this._port.open({ baudRate: baud });
      this._setupStreams();

      try {
        const match = await this._helloHandshake(onProgress);
        this._negotiatedBaud = baud;
        this._deviceInfo = { brand: match[1], protocolVersion: parseInt(match[2], 10) };
        // Discard any stale responses still in the pipe (e.g. a duplicate
        // OK:HELLO left over from a prior handshake or the DTR/RTS reset
        // that opening the port triggers) so they don't desync the first
        // real command.
        await this._drainInput();
        this._connected = true;
        this._startReadLoop();
        this.dispatchEvent(new Event('connected'));
        return;
      } catch (err) {
        lastError = err;
        // This baud didn't work — release streams and close before trying
        // the next one. Web Serial requires a close/reopen to change baud,
        // and we must not leave the port locked/half-open.
        await this._closePort();
      }
    }

    throw lastError || new Error('Failed to connect after 3 attempts');
  }

  /**
   * Robust handshake: try HELLO up to HELLO_MAX_ATTEMPTS times at whatever
   * baud the port is currently open at. On reconnect the device may have
   * stale data in its serial buffer. HELLO is accepted by the firmware
   * regardless of command mode state. Strategy: send newlines to flush
   * partial lines, send HELLO, read response. If we get a non-HELLO
   * response, clear buffer and retry.
   * @returns {Promise<RegExpMatchArray>} the OK:HELLO:<brand>:<proto>:<baud> match
   * @throws on failure after all attempts
   */
  async _helloHandshake(onProgress) {
    let lastError = null;
    for (let attempt = 0; attempt < HELLO_MAX_ATTEMPTS; attempt++) {
      try {
        // Report progress (1-based) so the UI can show "attempt N of M".
        if (onProgress) onProgress(attempt + 1, HELLO_MAX_ATTEMPTS);

        // Clear our internal buffer
        this._buffer = new Uint8Array(0);

        // Poke the device across the attempt window instead of firing one HELLO
        // and waiting. Each probe flushes any partial line then sends HELLO, and
        // we read in short slices so a device that becomes ready mid-window is
        // caught within POKE_INTERVAL_MS rather than after the whole attempt.
        const deadline = Date.now() + COMMAND_TIMEOUT_MS;
        while (Date.now() < deadline) {
          // Flush any partial command on the device, then probe.
          try {
            const encoder = new TextEncoder();
            await this._writer.write(encoder.encode('\n\n\n'));
          } catch (_) {}
          await this._write('HELLO\n');

          // Read lines until this probe's slice elapses, skipping stale
          // responses (OK:BYE, ERR:BADCMD, boot noise). On a slice timeout we
          // fall through and poke again; _readWithTimeout preserves the pending
          // read so no bytes are dropped between probes.
          const sliceDeadline = Math.min(deadline, Date.now() + HELLO_POKE_INTERVAL_MS);
          while (Date.now() < sliceDeadline) {
            let line;
            try {
              line = await this._readLine(sliceDeadline - Date.now());
            } catch (_) {
              break; // slice quiet → re-poke
            }
            const match = line.match(/^OK:HELLO:(\w+):(\d+):(\d+)$/);
            if (match) return match;
          }
        }
        lastError = new Error('HELLO response not received');
      } catch (err) {
        lastError = err;
        this._buffer = new Uint8Array(0);
      }
    }
    throw lastError || new Error('Failed to connect after 3 attempts');
  }

  /**
   * Send BYE and close the port.
   */
  async disconnect() {
    if (!this._connected) return;
    try {
      this._buffer = new Uint8Array(0);
      await this._write('BYE\n');
      await this._readLine();
    } catch (_) {
      // best-effort
    }
    this._connected = false;
    this._buffer = new Uint8Array(0);
    this._commandQueue = Promise.resolve();
    await this._cleanup();
    this.dispatchEvent(new Event('disconnected'));
  }

  /**
   * Whether the client currently has an open connection.
   * @returns {boolean}
   */
  isConnected() {
    return this._connected;
  }

  /**
   * Protocol version negotiated during the HELLO handshake, or null if not
   * connected. >= 2 means the device supports the reliable v2 block OTA; 1 is
   * legacy firmware (steer those to the ESP Web Tools browser installer).
   * @returns {number|null}
   */
  protocolVersion() {
    return this._deviceInfo ? this._deviceInfo.protocolVersion : null;
  }

  /**
   * Tear down the connection without the BYE handshake. Used when the device
   * reboots (e.g. after a flash) and a graceful disconnect is impossible:
   * cancels any in-flight read so the stuck reader unblocks, then closes the
   * port. Best-effort and never throws, so it can't leave the port locked open
   * and block the next connect.
   */
  async forceClose() {
    this._connected = false;
    this._pendingRead = null;
    this._buffer = new Uint8Array(0);
    this._commandQueue = Promise.resolve();
    if (this._reader) {
      try { await this._reader.cancel(); } catch (_) {}
      try { this._reader.releaseLock(); } catch (_) {}
      this._reader = null;
    }
    if (this._writer) {
      try { await this._writer.close(); } catch (_) {}
      try { this._writer.releaseLock(); } catch (_) {}
      this._writer = null;
    }
    if (this._port) {
      try { await this._port.close(); } catch (_) {}
      this._port = null;
    }
  }

  /**
   * Enqueue a command so only one runs at a time over serial.
   */
  _enqueue(fn) {
    this._commandQueue = this._commandQueue.then(() => fn(), () => fn());
    return this._commandQueue;
  }

  // ---- Commands (all serialized via _enqueue) ----

  ping() {
    return this._enqueue(async () => {
      this._assertConnected();
      await this._write('PING\n');
      const line = await this._readLine();
      if (line !== 'OK:PING') throw new Error(`Unexpected PING response: ${line}`);
      return true;
    });
  }

  /**
   * Recalibrate the analog gas sensors (CO, mercaptan) against clean ambient
   * air — captures a fresh per-device R0 baseline into NVS. Admin action; the
   * device must be in known-clean air. Resolves true on OK:CALGAS.
   */
  calibrateGas() {
    return this._enqueue(async () => {
      this._assertConnected();
      await this._write('CALGAS\n');
      const line = await this._readLine();
      if (line.startsWith('ERR:')) throw this._parseError(line);
      if (line !== 'OK:CALGAS') throw new Error(`Unexpected CALGAS response: ${line}`);
      return true;
    });
  }

  // ---- Device settings (name / polling / time / CO2 recal) ----

  // Query the firmware's settings-schema version. Resolves to the integer
  // version on success, or null when the firmware doesn't support remote
  // settings at all — firmware predating the SETTINGS command answers
  // ERR:BADCMD. The Settings tab uses this to decide between rendering the
  // settings cards and showing an "update firmware" message, so it never
  // renders cards whose serial commands the device would reject.
  getSettingsVersion() {
    return this._enqueue(async () => {
      this._assertConnected();
      await this._write('SETTINGS\n');
      const line = await this._readLine();
      if (line.startsWith('ERR:')) return null;
      const m = line.match(/^OK:SETTINGS:(\d+)$/);
      if (!m) throw new Error(`Unexpected SETTINGS response: ${line}`);
      return parseInt(m[1], 10);
    });
  }

  getName() {
    return this._enqueue(async () => {
      this._assertConnected();
      await this._write('GETNAME\n');
      const line = await this._readLine();
      if (line.startsWith('ERR:')) throw this._parseError(line);
      const m = line.match(/^OK:NAME:(.*)$/);
      if (!m) throw new Error(`Unexpected GETNAME response: ${line}`);
      return m[1];
    });
  }

  setName(name) {
    return this._enqueue(async () => {
      this._assertConnected();
      await this._write(`SETNAME ${name}\n`);
      const line = await this._readLine();
      if (line.startsWith('ERR:')) throw this._parseError(line);
      if (line !== 'OK:SETNAME') throw new Error(`Unexpected SETNAME response: ${line}`);
      return true;
    });
  }

  setSsid(ssid) {
    return this._enqueue(async () => {
      this._assertConnected();
      await this._write(`SETSSID ${ssid}\n`);
      const line = await this._readLine();
      if (line.startsWith('ERR:')) throw this._parseError(line);
      if (line !== 'OK:SETSSID') throw new Error(`Unexpected SETSSID response: ${line}`);
      return true;
    });
  }

  setPass(pass) {
    return this._enqueue(async () => {
      this._assertConnected();
      await this._write(`SETPASS ${pass}\n`);
      const line = await this._readLine();
      if (line.startsWith('ERR:')) throw this._parseError(line);
      if (line !== 'OK:SETPASS') throw new Error(`Unexpected SETPASS response: ${line}`);
      return true;
    });
  }

  setToken(token) {
    return this._enqueue(async () => {
      this._assertConnected();
      await this._write(`SETTOKEN ${token}\n`);
      const line = await this._readLine();
      if (line.startsWith('ERR:')) throw this._parseError(line);
      if (line !== 'OK:SETTOKEN') throw new Error(`Unexpected SETTOKEN response: ${line}`);
      return true;
    });
  }

  getPolling() {
    return this._enqueue(async () => {
      this._assertConnected();
      await this._write('GETPOLL\n');
      const line = await this._readLine();
      if (line.startsWith('ERR:')) throw this._parseError(line);
      const m = line.match(/^OK:POLL:(\d+)$/);
      if (!m) throw new Error(`Unexpected GETPOLL response: ${line}`);
      return parseInt(m[1], 10);
    });
  }

  setPolling(seconds) {
    return this._enqueue(async () => {
      this._assertConnected();
      await this._write(`SETPOLL ${seconds}\n`);
      const line = await this._readLine();
      if (line.startsWith('ERR:')) throw this._parseError(line);
      if (line !== 'OK:SETPOLL') throw new Error(`Unexpected SETPOLL response: ${line}`);
      return true;
    });
  }

  // Resolves to the device epoch (seconds) or null if the clock is unsynced.
  getTime() {
    return this._enqueue(async () => {
      this._assertConnected();
      await this._write('GETTIME\n');
      const line = await this._readLine();
      if (line.startsWith('ERR:')) throw this._parseError(line);
      if (line === 'OK:TIME:NOSYNC') return null;
      const m = line.match(/^OK:TIME:(\d+)$/);
      if (!m) throw new Error(`Unexpected GETTIME response: ${line}`);
      return parseInt(m[1], 10);
    });
  }

  // Push the current wall-clock (or a supplied epoch) to the device RTC.
  syncTime(epochSeconds = undefined) {
    const epoch = epochSeconds ?? Math.floor(Date.now() / 1000);
    return this._enqueue(async () => {
      this._assertConnected();
      await this._write(`SETTIME ${epoch}\n`);
      const line = await this._readLine();
      if (line.startsWith('ERR:')) throw this._parseError(line);
      if (line !== 'OK:SETTIME') throw new Error(`Unexpected SETTIME response: ${line}`);
      return true;
    });
  }

  calibrateCo2(targetPpm) {
    return this._enqueue(async () => {
      this._assertConnected();
      await this._write(`CALCO2 ${targetPpm}\n`);
      const line = await this._readLine();
      if (line.startsWith('ERR:')) throw this._parseError(line);
      if (line !== 'OK:CALCO2') throw new Error(`Unexpected CALCO2 response: ${line}`);
      return true;
    });
  }

  info() {
    return this._enqueue(async () => {
      this._assertConnected();
      await this._write('INFO\n');
      const line = await this._readLine();
      const match = line.match(/^OK:INFO:(.+)$/);
      if (!match) throw new Error(`Unexpected INFO response: ${line}`);
      return JSON.parse(match[1]);
    });
  }

  readings() {
    return this._enqueue(async () => {
      this._assertConnected();
      await this._write('READINGS\n');
      const line = await this._readLine();
      if (line.startsWith('ERR:NODATA')) throw new ReadingsError('warming-up');
      if (line.startsWith('ERR:BADCMD')) throw new ReadingsError('needs-update');
      const m = line.match(/^OK:READINGS:(.+)$/);
      if (!m) throw new ReadingsError('error');
      try {
        return mapReadings(JSON.parse(m[1]));
      } catch (_) {
        throw new ReadingsError('error');
      }
    });
  }

  free() {
    return this._enqueue(async () => {
      this._assertConnected();
      await this._write('FREE\n');
      const line = await this._readLine();
      const match = line.match(/^OK:FREE:(\d+)\t(\d+)$/);
      if (!match) throw new Error(`Unexpected FREE response: ${line}`);
      return { used: parseInt(match[1], 10), total: parseInt(match[2], 10) };
    });
  }

  listFiles(path) {
    return this._enqueue(async () => {
      this._assertConnected();
      await this._write(`LS ${path}\n`);
      const header = await this._readLine();
      const headerMatch = header.match(/^OK:LS:(\d+)$/);
      if (!headerMatch) throw new Error(`Unexpected LS response: ${header}`);

      const count = parseInt(headerMatch[1], 10);
      const files = [];
      for (let i = 0; i < count; i++) {
        const entry = await this._readLine();
        const parts = entry.split('\t');
        files.push({ name: parts[0], size: parseInt(parts[1], 10) });
      }

      const end = await this._readLine();
      if (end !== 'END') throw new Error(`Expected END, got: ${end}`);
      return files;
    });
  }

  fileSize(path) {
    return this._enqueue(async () => {
      this._assertConnected();
      await this._write(`SIZE ${path}\n`);
      const line = await this._readLine();

      if (line.startsWith('ERR:')) {
        throw this._parseError(line);
      }

      const match = line.match(/^OK:SIZE:(\d+)$/);
      if (!match) throw new Error(`Unexpected SIZE response: ${line}`);
      return parseInt(match[1], 10);
    });
  }

  readFile(path, onProgress) {
    return this._enqueue(async () => {
      this._assertConnected();
      await this._write(`READ ${path}\n`);
      const header = await this._readLine(TRANSFER_TIMEOUT_MS);

      if (header.startsWith('ERR:')) {
        throw this._parseError(header);
      }

      const headerMatch = header.match(/^OK:READ:(.+)\t(\d+)$/);
      if (!headerMatch) throw new Error(`Unexpected READ response: ${header}`);

      const totalSize = parseInt(headerMatch[2], 10);

      // Read exactly totalSize bytes of raw file data
      const data = await this._readExactBytes(totalSize, (received) => {
        if (onProgress) onProgress(received, totalSize);
      });

      // Read the END:<crc> line
      const endLine = await this._readLine(TRANSFER_TIMEOUT_MS);
      if (!endLine.startsWith('END:')) {
        throw new Error(`Expected END, got: ${endLine}`);
      }

      const expectedCrc = endLine.split(':')[1].toLowerCase();
      const actualCrc = crc32hex(data);
      if (expectedCrc !== actualCrc) {
        throw new Error(`CRC mismatch: expected ${expectedCrc}, got ${actualCrc}`);
      }

      return data;
    });
  }

  deleteFile(path) {
    return this._enqueue(async () => {
      this._assertConnected();
      await this._write(`DELETE ${path}\n`);
      const line = await this._readLine();

      if (line.startsWith('ERR:')) {
        throw this._parseError(line);
      }

      if (line !== 'OK:DELETE') throw new Error(`Unexpected DELETE response: ${line}`);
      return true;
    });
  }

  /**
   * Flash new firmware over serial OTA.
   * @param {Uint8Array} binaryData - The firmware .bin file contents
   * @param {function} onProgress - Callback: (phase, received, total) => void
   *   phase: 'starting' | 'uploading' | 'verifying' | 'committing' | 'rebooting'
   * @returns {Promise<void>}
   */
  flashFirmware(binaryData, onProgress) {
    return this._enqueue(async () => {
      this._assertConnected();
      // Devices advertising protocol >= 2 support the reliable block protocol
      // (per-block CRC + ack + retry). Older devices get the legacy stream.
      if (this._deviceInfo && this._deviceInfo.protocolVersion >= 2) {
        return this._flashFirmwareV2(binaryData, onProgress);
      }
      return this._flashFirmwareV1(binaryData, onProgress);
    });
  }

  /**
   * Open a resumable OTA session. Returns the byte offset to start streaming
   * from.
   * New firmware: OTARESUME <size> <crc> -> OK:OTA:RESUME:<n> (n = 0 fresh,
   *   or the offset to resume from).
   * Old firmware: ERR:BADCMD (or any other non-RESUME reply) -> fall back to
   *   the legacy OTABEGIN handshake and start at 0.
   *
   * Resume is device-session-scoped, not persisted on the web side: the
   * device keeps the resume offset in RAM for the life of its OTA session, so
   * this survives a page reload or a serial replug, but a device reset (power
   * cycle / firmware watchdog) drops the session and the next attempt starts
   * fresh from offset 0.
   */
  async _openOtaSession(size, crc) {
    await this._write(`OTARESUME ${size} ${crc}\n`);
    let resp = '';
    try {
      resp = await this._readLine(TRANSFER_TIMEOUT_MS);
    } catch (err) {
      this._log('rx', `OTARESUME read failed (${err.message}); falling back to OTABEGIN`, 'binary');
      resp = '';
    }
    const m = /^OK:OTA:RESUME:(\d+)$/.exec(resp);
    if (m) return parseInt(m[1], 10);

    // Not supported / not a resume reply -> legacy open from 0.
    await this._write(`OTABEGIN ${size}\n`);
    const ready = await this._readLine(TRANSFER_TIMEOUT_MS);
    if (ready.startsWith('ERR:')) throw this._parseError(ready);
    if (ready !== 'OK:OTA:READY') throw new Error(`Unexpected OTA response: ${ready}`);
    return 0;
  }

  /**
   * Reliable block-based OTA: send the image as 1 KB CRC'd blocks, wait for the
   * device to ack each one (flow control), and retry any block it rejects or
   * that times out. Tolerates serial glitches the v1 stream could not.
   */
  async _flashFirmwareV2(binaryData, onProgress) {
    const progress = onProgress || (() => {});
    const BLOCK = 1024;
    const MAX_RETRIES = 5;
    const imageCrc = crc32hex(binaryData).toUpperCase();

    progress('starting', 0, binaryData.length);
    const startOffset = await this._openOtaSession(binaryData.length, imageCrc);
    progress('uploading', startOffset, binaryData.length);

    for (let offset = startOffset; offset < binaryData.length; offset += BLOCK) {
      const end = Math.min(offset + BLOCK, binaryData.length);
      const block = binaryData.slice(offset, end);
      const crc = crc32hex(block).toUpperCase();
      const expectedAck = `OK:OTAB:${crc}`;

      let acked = false;
      let lastError = null;
      for (let attempt = 0; attempt < MAX_RETRIES && !acked; attempt++) {
        await this._write(`OTAB ${block.length} ${crc}\n`);
        await this._writer.write(block);
        let resp;
        try {
          resp = await this._readLine(TRANSFER_TIMEOUT_MS);
        } catch (err) {
          lastError = err; // timeout — retry the block
          continue;
        }
        if (resp === expectedAck) {
          acked = true;
        } else if (resp.startsWith('ERR:OTAB')) {
          lastError = this._parseError(resp); // device rejected — retry
        } else if (resp.startsWith('ERR:')) {
          throw this._parseError(resp); // fatal (e.g. ERR:OTAB:NOT_ACTIVE handled above; OVERFLOW etc.)
        } else {
          lastError = new Error(`Unexpected block response: ${resp}`);
        }
      }
      if (!acked) throw lastError || new Error('Firmware block failed after retries');
      progress('uploading', end, binaryData.length);
    }

    progress('committing', binaryData.length, binaryData.length);
    await this._write('OTA_COMMIT\n');
    const commitResp = await this._readLine(TRANSFER_TIMEOUT_MS);
    if (commitResp.startsWith('ERR:')) throw this._parseError(commitResp);
    if (commitResp !== 'OK:OTA:REBOOTING') {
      throw new Error(`Unexpected commit response: ${commitResp}`);
    }
    progress('rebooting', binaryData.length, binaryData.length);
    this._connected = false;
  }

  async _flashFirmwareV1(binaryData, onProgress) {
    {
      const progress = onProgress || (() => {});

      // Phase 1: Send OTA command
      progress('starting', 0, binaryData.length);
      await this._write(`OTA ${binaryData.length}\n`);
      const ready = await this._readLine(TRANSFER_TIMEOUT_MS);

      if (ready.startsWith('ERR:')) {
        throw this._parseError(ready);
      }
      if (ready !== 'OK:OTA:READY') {
        throw new Error(`Unexpected OTA response: ${ready}`);
      }

      // Phase 2: Stream binary data in chunks
      const CHUNK = 512;
      for (let offset = 0; offset < binaryData.length; offset += CHUNK) {
        const end = Math.min(offset + CHUNK, binaryData.length);
        const chunk = binaryData.slice(offset, end);
        await this._writer.write(chunk);
        progress('uploading', end, binaryData.length);
      }
      this._log('tx', `[${binaryData.length} bytes firmware data]`, 'binary');

      // Phase 3: Wait for device to confirm receipt + CRC
      progress('verifying', binaryData.length, binaryData.length);
      // Timeout scales with file size: base 15s + 1s per 10KB
      const receiveTimeout = TRANSFER_TIMEOUT_MS + Math.ceil(binaryData.length / 10240) * 1000;
      const received = await this._readLine(receiveTimeout);

      if (received.startsWith('ERR:')) {
        throw this._parseError(received);
      }

      const recvMatch = received.match(/^OK:OTA:RECEIVED:([0-9A-Fa-f]+)$/);
      if (!recvMatch) {
        throw new Error(`Unexpected OTA RECEIVED response: ${received}`);
      }

      // Verify CRC matches
      const deviceCrc = recvMatch[1].toLowerCase();
      const localCrc = crc32hex(binaryData);
      if (deviceCrc !== localCrc) {
        await this._write('OTA_ABORT\n');
        await this._readLine().catch(() => {}); // best-effort read OK:OTA:ABORTED
        throw new Error(`Firmware CRC mismatch: device=${deviceCrc}, local=${localCrc}`);
      }

      // Phase 4: Commit
      progress('committing', binaryData.length, binaryData.length);
      await this._write('OTA_COMMIT\n');
      const commitResp = await this._readLine(TRANSFER_TIMEOUT_MS);

      if (commitResp !== 'OK:OTA:REBOOTING') {
        throw new Error(`Unexpected commit response: ${commitResp}`);
      }

      // Device is rebooting — connection will drop
      progress('rebooting', binaryData.length, binaryData.length);
      this._connected = false;
    }
  }

  // ---- Internal ----

  _assertConnected() {
    if (!this._connected) throw new Error('Not connected');
  }

  _setupStreams() {
    this._reader = this._port.readable.getReader();
    this._writer = this._port.writable.getWriter();
    this._buffer = new Uint8Array(0);
    this._pendingRead = null;
  }

  async _releaseStreams() {
    this._pendingRead = null;
    if (this._reader) {
      try { this._reader.releaseLock(); } catch (_) {}
      this._reader = null;
    }
    if (this._writer) {
      try { this._writer.releaseLock(); } catch (_) {}
      this._writer = null;
    }
  }

  async _closePort() {
    await this._releaseStreams();
    try { await this._port.close(); } catch (_) {}
  }

  async _cleanup() {
    await this._closePort();
    this._port = null;
  }

  /**
   * Read exactly `count` bytes from the stream. Calls onProgress(received)
   * periodically. Returns a Uint8Array.
   */
  async _readExactBytes(count, onProgress) {
    const result = new Uint8Array(count);
    let offset = 0;
    const deadline = Date.now() + TRANSFER_TIMEOUT_MS + (count / 10); // scale timeout with file size

    while (offset < count) {
      // Use bytes already in our buffer first
      if (this._buffer.length > 0) {
        const needed = count - offset;
        const available = Math.min(this._buffer.length, needed);
        result.set(this._buffer.slice(0, available), offset);
        this._buffer = this._buffer.slice(available);
        offset += available;
        if (onProgress) onProgress(offset);
        continue;
      }

      if (Date.now() > deadline) {
        throw new Error(`Transfer timed out at ${offset}/${count} bytes`);
      }

      const remaining = deadline - Date.now();
      const readResult = await this._readWithTimeout(remaining);
      if (readResult.done) throw new Error('Stream closed during transfer');
      this._buffer = this._concatBuffers(this._buffer, readResult.value);
    }

    this._log('rx', `[${count} bytes]`, 'binary');
    return result;
  }

  /**
   * Drain any pending bytes from the read buffer (boot messages, noise).
   * Sends a blank line first to flush any partial command on the device,
   * then discards whatever comes back.
   */
  async _drain() {
    // Send a bare newline to flush any partial line on the device side
    try {
      const encoder = new TextEncoder();
      await this._writer.write(encoder.encode('\n'));
    } catch (_) {}

    // Discard whatever is in our internal buffer
    this._buffer = new Uint8Array(0);
  }

  _redactForLog(line) {
    // SETPASS/SETTOKEN carry the WiFi password and device bearer token —
    // never surface them in the on-screen serial log.
    const m = line.match(/^(SETPASS|SETTOKEN) /);
    return m ? `${m[1]} ****` : line;
  }

  async _write(text) {
    this._log('tx', this._redactForLog(text.replace(/\n$/, '')));
    const data = new TextEncoder().encode(text);
    await this._writer.write(data);
  }

  /**
   * Read and discard everything currently in the pipe, returning once the
   * stream has been quiet for `quietMs`. Used right after the HELLO handshake
   * to drop stale/duplicate responses before issuing real commands.
   */
  async _drainInput(quietMs = 150) {
    this._buffer = new Uint8Array(0);
    for (;;) {
      let result;
      try {
        result = await this._readWithTimeout(quietMs);
      } catch (_) {
        break; // timed out with no data → pipe is quiet
      }
      if (result.done) break;
      // discard result.value
    }
    this._buffer = new Uint8Array(0);
  }

  /**
   * Read from the buffer/stream until a newline is found.
   * Returns the line WITHOUT the trailing newline.
   * Throws on timeout.
   */
  async _readLine(timeoutMs = COMMAND_TIMEOUT_MS) {
    const deadline = Date.now() + timeoutMs;

    while (true) {
      // Check buffer for a complete line
      const nlIndex = this._findNewline(this._buffer);
      if (nlIndex !== -1) {
        const line = new TextDecoder().decode(this._buffer.slice(0, nlIndex));
        this._buffer = this._buffer.slice(nlIndex + 1);

        // Skip empty lines (stray newlines from serial noise)
        if (line.trim() === '') continue;

        // Skip ESP32 debug log lines (e.g. "[ 50183][E][vfs_api.cpp:105] ...")
        // These leak to Serial from the ESP-IDF VFS layer, bypassing our mute
        if (/^\[\s*\d+\]\[/.test(line)) {
          this._log('rx', line, 'binary'); // log it as noise, don't return it
          continue;
        }

        this._log('rx', line);
        return line;
      }

      if (Date.now() > deadline) {
        throw new Error('Command timed out');
      }

      // Read more data
      const remaining = deadline - Date.now();
      const readResult = await this._readWithTimeout(remaining);
      if (readResult.done) {
        throw new Error('Stream closed');
      }
      this._buffer = this._concatBuffers(this._buffer, readResult.value);
    }
  }


  _findNewline(buf) {
    for (let i = 0; i < buf.length; i++) {
      if (buf[i] === 0x0a) return i; // \n
    }
    return -1;
  }

  _concatBuffers(a, b) {
    const result = new Uint8Array(a.length + b.length);
    result.set(a, 0);
    result.set(b, a.length);
    return result;
  }

  async _readWithTimeout(ms) {
    // Reuse an in-flight read across timeouts. A bare Promise.race would leave
    // the underlying reader.read() pending when the timeout wins, and that
    // abandoned read would steal the next chunk — desyncing later commands.
    if (!this._pendingRead) {
      this._pendingRead = this._reader.read();
    }
    const pending = this._pendingRead;
    let timer;
    const timeout = new Promise((_, reject) => {
      timer = setTimeout(() => reject(new Error('Command timed out')), Math.max(ms, 0));
    });
    try {
      const result = await Promise.race([pending, timeout]);
      this._pendingRead = null; // read resolved — consumed
      return result;
    } catch (err) {
      if (err.message === 'Command timed out') {
        throw err; // keep _pendingRead so the next call reuses it
      }
      this._pendingRead = null; // genuine stream error — drop it
      throw err;
    } finally {
      clearTimeout(timer);
    }
  }

  _parseError(line) {
    // ERR:<CODE>:<message>
    const parts = line.match(/^ERR:(\w+):(.*)$/);
    if (parts) {
      const err = new Error(parts[2]);
      err.code = parts[1];
      return err;
    }
    return new Error(line);
  }

  _startReadLoop() {
    // Disconnect detection is implicit — when the USB cable is
    // unplugged, the next _readWithTimeout call will throw
    // 'Stream closed' which propagates to the caller.
  }
}

export default SerialClient;
